import React, { useState } from 'react';
import CinderCrownCover from './CinderCrownCover';
import FiveMinuteRewindCover from './FiveMinuteRewindCover';
import GirlWhoCouldTasteLiesCover from './GirlWhoCouldTasteLiesCover';
import SnaggleToothedBeastCover from './SnaggleToothedBeastCover';

interface BookCoverProps {
  book: any;
  className?: string;
}

const getBookFallbackImage = (title: string) => {
  const t = title.toLowerCase();
  if (t.includes('pip') || t.includes('pipkin')) {
    return 'https://picsum.photos/seed/flight/450/650';
  }
  if (t.includes('meadow') || t.includes('moonberry') || t.includes('muddle')) {
    return 'https://picsum.photos/seed/meadow/450/650';
  }
  if (t.includes('sleuths') || t.includes('stott')) {
    return 'https://picsum.photos/seed/sleuths/450/650';
  }
  if (t.includes('vow') || t.includes('traitor')) {
    return 'https://picsum.photos/seed/vow/450/650';
  }
  if (t.includes('lies') || t.includes('taste')) {
    return 'https://picsum.photos/seed/lies/450/650';
  }
  if (t.includes('rewind')) {
    return 'https://picsum.photos/seed/rewind/450/650';
  }
  return 'https://picsum.photos/seed/book/450/650';
};

export default function BookCover({ book, className = "w-full h-full object-cover" }: BookCoverProps) {
  const [hasError, setHasError] = useState(false);

  // Directly show the custom vector cover for b13 matching the user-uploaded design.
  if (book.id === 'b13') {
    return <SnaggleToothedBeastCover />;
  }

  // Attempt to load the original image directly without modifying its appearance.
  // This satisfies the user's primary intent to show the authentic physical image covers.
  if (book.image && !hasError) {
    if (book.id === 'b3') {
      return (
        <div className="w-full h-full overflow-hidden relative rounded-2xl bg-neutral-100">
          <img
            src={book.image}
            alt={book.title}
            referrerPolicy="no-referrer"
            onError={() => setHasError(true)}
            className={`${className} max-w-none`}
            style={{
              width: '200%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: 'right center',
            }}
          />
        </div>
      );
    }
    if (book.id === 'b10') {
      return (
        <div className="w-full h-full overflow-hidden relative rounded-2xl bg-neutral-100">
          <img
            src={book.image}
            alt={book.title}
            referrerPolicy="no-referrer"
            onError={() => setHasError(true)}
            className={`${className} scale-[1.28] transform origin-center`}
          />
        </div>
      );
    }
    return (
      <picture>
        <source srcSet={book.image.replace(/\.(png|jpg|jpeg)$/, '.webp')} type="image/webp" />
        <img
          src={book.image}
          alt={book.title}
          referrerPolicy="no-referrer"
          onError={() => setHasError(true)}
          className={className}
        />
      </picture>
    );
  }

  // Fallback beautiful vector representations only when the physical image fails to load.
  if (book.id === 'b6') {
    return <CinderCrownCover />;
  }
  if (book.id === 'b7') {
    return <FiveMinuteRewindCover />;
  }
  if (book.id === 'b8') {
    return <GirlWhoCouldTasteLiesCover />;
  }

  // Generic flat colors for other text-based titles without a custom illustration cover
  return (
    <div className={`w-full h-full ${book.color || 'bg-slate-900'} flex items-center justify-center p-8`}>
      <div className="w-full h-full border border-white/20 rounded flex flex-col justify-between p-6">
        <div className="text-white/60 text-[10px] font-medium tracking-widest uppercase">{book.author}</div>
        <h3 className="font-serif text-xl font-semibold text-white leading-tight">{book.title}</h3>
      </div>
    </div>
  );
}
